import { GroupBase, OptionTypeBase } from '@cad-arc';
import { debounce, noop, partition, sortBy } from 'lodash';
import { Unit, UnitOption, UnitResource } from '@mark43/cad-api';
import { ComponentPropsWithoutRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { adaptSelectOnChange } from '../helpers/adapt-select-on-change';
import { useFocusManager } from '@react-aria/focus';
import { moveFocusNext } from '../helpers/move-focus';
import { BaseSelect } from './base-select';

function mapUnitToUnitOption(unit: Unit): UnitOption {
    return {
        label: unit.fullCallSign,
        value: unit.id,
    };
}

interface UnitSelectProps
    extends Omit<ComponentPropsWithoutRef<typeof BaseSelect>, 'options' | 'isLoading'> {
    dispatchAreaIds: number[];
    agencyTypeGlobalAttrIds?: number[];
}

const UNITS_GROUP_LABEL = 'Units';
const TEMPORARY_UNITS_GROUP_LABEL = 'Temporary Units';

export function UnitSelect({
    dispatchAreaIds,
    agencyTypeGlobalAttrIds,
    ...rest
}: UnitSelectProps): React.ReactElement {
    const apiClient = useApiClient();
    const { data: units, isInitialLoading, refetch } = useQuery(['all-units'], () => {
        return apiClient
            .request<UnitResource.GetAllUnits>({ url: 'unit', method: 'GET' })
            .then((resp) => resp.data.data);
    });
    const options: [GroupBase<OptionTypeBase>, GroupBase<OptionTypeBase>] = useMemo(() => {
        if (!units) {
            return [
                { label: UNITS_GROUP_LABEL, options: [] },
                { label: TEMPORARY_UNITS_GROUP_LABEL, options: [] },
            ];
        }
        let filteredUnits = units;
        if (agencyTypeGlobalAttrIds) {
            filteredUnits = units.filter((unit) =>
                agencyTypeGlobalAttrIds.includes(unit.agencyTypeGlobalAttrId)
            );
        }
        const [fullUnits, temporaryUnits] = partition(filteredUnits, (unit) => !unit.isTemporary);
        const [fullUnitsInDispatchAreas, fullUnitsNotInDispatchAreas] = partition(
            fullUnits,
            (unit) => {
                const effectiveDispatchAreaId = unit.dispatchAreaOverrideId ?? unit.dispatchAreaId;
                return effectiveDispatchAreaId && dispatchAreaIds.includes(effectiveDispatchAreaId);
            }
        );
        const sortedFullUnitsInDispatchAreas = sortBy(fullUnitsInDispatchAreas, 'fullCallSign');
        const sortedFullUnitsNotInDispatchAreas = sortBy(
            fullUnitsNotInDispatchAreas,
            'fullCallSign'
        );

        const [temporaryUnitsInDispatchAreas, temporaryUnitsNotInDispatchAreas] = partition(
            temporaryUnits,
            (unit) => {
                const effectiveDispatchAreaId = unit.dispatchAreaOverrideId ?? unit.dispatchAreaId;
                return effectiveDispatchAreaId && dispatchAreaIds.includes(effectiveDispatchAreaId);
            }
        );
        const sortedTemporaryUnitsInDispatchAreas = sortBy(
            temporaryUnitsInDispatchAreas,
            'fullCallSign'
        );
        const sortedTemporaryUnitsNotInDispatchAreas = sortBy(
            temporaryUnitsNotInDispatchAreas,
            'fullCallSign'
        );
        return [
            {
                label: UNITS_GROUP_LABEL,
                options: sortedFullUnitsInDispatchAreas
                    .concat(sortedFullUnitsNotInDispatchAreas)
                    .map(mapUnitToUnitOption),
            },
            {
                label: TEMPORARY_UNITS_GROUP_LABEL,
                options: sortedTemporaryUnitsInDispatchAreas
                    .concat(sortedTemporaryUnitsNotInDispatchAreas)
                    .map(mapUnitToUnitOption),
            },
        ];
    }, [dispatchAreaIds, units, agencyTypeGlobalAttrIds]);

    const flatOptions = useMemo(() => [...options[0].options, ...options[1].options], [options]);

    const debouncedRefetchUnits = useMemo(() => debounce(refetch, 1000, { leading: true }), [
        refetch,
    ]);
    const focusManager = useFocusManager();

    return (
        <BaseSelect
            options={options}
            isLoading={isInitialLoading}
            {...rest}
            onChange={(value) => {
                adaptSelectOnChange(rest.onChange ?? noop, flatOptions)(value);
                moveFocusNext(focusManager, value.currentTarget.value, false);
            }}
            onMenuOpen={debouncedRefetchUnits}
        />
    );
}
